import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Trophy, Medal, Award } from 'lucide-react'

export default function LeaderboardCard({ data = [], loading = false }) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Top Performing Months
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse">Loading leaderboard...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Top Performing Months
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No data available for leaderboard
          </div>
        </CardContent>
      </Card>
    )
  }

  // Sort data by volume (descending) and take top 10
  const sortedData = [...data]
    .sort((a, b) => b.volume - a.volume)
    .slice(0, 10)

  // Format month for display (convert from "Jan 2024" to "Jan-24")
  const formatMonth = (monthString) => {
    const [month, year] = monthString.split(' ')
    const shortYear = year.slice(-2)
    return `${month}-${shortYear}`
  }

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  // Get rank icon
  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-4 w-4 text-yellow-500" />
      case 2:
        return <Medal className="h-4 w-4 text-gray-400" />
      case 3:
        return <Award className="h-4 w-4 text-orange-600" />
      default:
        return <div className="h-4 w-4 flex items-center justify-center text-xs font-bold text-muted-foreground">{rank}</div>
    }
  }

  // Get rank styling
  const getRankStyling = () => {
    return "hover:bg-muted/50"
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Top Performing Months
          </div>
          <Badge variant="secondary">
            Top {sortedData.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-0">
          {/* Header */}
          <div className="grid grid-cols-12 gap-4 pb-2 border-b text-sm font-semibold text-muted-foreground">
            <div className="col-span-1">Rank</div>
            <div className="col-span-3">Month</div>
            <div className="col-span-4 text-right">Vol (kg)</div>
            <div className="col-span-4 text-right">Val (£)</div>
          </div>

          {/* Leaderboard entries */}
          {sortedData.map((entry, index) => {
            const rank = index + 1
            return (
              <div
                key={entry.month}
                className={`grid grid-cols-12 gap-4 py-1 px-3 rounded-lg transition-all ${getRankStyling(rank)}`}
              >
                <div className="col-span-1 flex items-center">
                  {getRankIcon(rank)}
                </div>
                <div className="col-span-3 flex items-center font-medium">
                  {formatMonth(entry.month)}
                </div>
                <div className="col-span-4 text-right font-mono">
                  {Math.round(entry.volume)} kg
                </div>
                <div className="col-span-4 text-right font-mono font-semibold">
                  {formatCurrency(entry.value)}
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}